/**
 * ListingForm Component
 * A reusable form component for creating and editing listings
 * Handles both new listings and editing existing ones
 * 
 * @param {Object} initialData - Data for editing an existing listing (optional)
 * @param {Function} onSubmit - Callback function when form is submitted
 * @param {Function} onCancel - Callback function when form is cancelled
 */
import React, { useState, useEffect } from 'react';
import MediaUpload from './MediaUpload';
import { FormControl, InputLabel, Select, MenuItem, OutlinedInput, InputAdornment, TextField, Radio, RadioGroup, FormControlLabel, Checkbox, Box } from '@mui/material';
import { useToast } from '../context/ToastContext';

function ListingForm({ initialData, onSubmit, onCancel, user, onPaymentSetup }) {
  const toast = useToast();
  // Initialize form state with default values
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'textbooks', // Default category
    condition: 'new', // Default condition
    price: '', // New price field
    negotiable: false,
    paymentMethod: 'direct', // Default to direct payment
    // Media fields
    images: [],
    video: null,
    thumbnailImage: null
  });

  // Populate form with initial data when editing an existing listing
  useEffect(() => {
    if (initialData) {
      console.log('📥 Loading initial data for editing:', {
        title: initialData.title,
        hasImages: !!initialData.images,
        imagesCount: initialData.images?.length || 0,
        hasVideo: !!initialData.video,
        videoType: initialData.video?.type,
        hasImageURL: !!initialData.imageURL,
        hasThumbnailImage: !!initialData.thumbnailImage
      });
      
      const formDataToSet = {
        title: initialData.title || '',
        description: initialData.description || '',
        category: initialData.category || 'textbooks',
        condition: initialData.condition || 'new',
        price: initialData.price || '',
        negotiable: initialData.negotiable || false,
        paymentMethod: initialData.paymentMethod || 'direct',
        // Handle legacy imageURL migration
        images: initialData.images || (initialData.imageURL ? [{
          url: initialData.imageURL,
          publicId: '',
          isThumnail: true
        }] : []),
        video: initialData.video || null,
        thumbnailImage: initialData.thumbnailImage || initialData.imageURL || null
      };
      
      console.log('📋 Setting form data:', {
        images: formDataToSet.images?.map(img => ({ url: img.url.substring(0, 50) + '...', publicId: img.publicId })),
        video: formDataToSet.video ? { 
          type: formDataToSet.video.type, 
          url: formDataToSet.video.url?.substring(0, 50) + '...' 
        } : null,
        thumbnailImage: formDataToSet.thumbnailImage?.substring(0, 50) + '...'
      });
      
      setFormData(formDataToSet);
    } else {
      console.log('📝 Creating new listing (no initial data)');
    }
  }, [initialData]);

  /**
   * Generic change handler for all form inputs
   * Handles both text inputs and checkboxes
   * @param {Event} e - The input change event
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // If user switches to "free" category, reset price & negotiable
    if (name === 'category' && value === 'free') {
      setFormData(prev => ({
        ...prev,
        category: 'free',
        price: '',
        negotiable: false
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  /**
   * Form submission handler
   * Prevents default form behavior and calls parent's onSubmit
   * @param {Event} e - The form submission event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('📝 ListingForm: Form submission started for:', initialData ? 'EDIT' : 'CREATE');
    console.log('📋 ListingForm: Form data being submitted:', {
      title: formData.title,
      description: formData.description.substring(0, 50) + '...',
      category: formData.category,
      condition: formData.condition,
      price: formData.price,
      negotiable: formData.negotiable,
      images: formData.images?.map(img => ({ url: img.url.substring(0, 50) + '...', publicId: img.publicId })),
      video: formData.video ? { 
        type: formData.video.type, 
        url: formData.video.url?.substring(0, 50) + '...',
        publicId: formData.video.publicId 
      } : null,
      thumbnailImage: formData.thumbnailImage?.substring(0, 50) + '...'
    });
    
    // Validate required fields
    if (!formData.title.trim()) {
      toast.warning('Title is required');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.warning('Description is required');
      return;
    }
    
    if (formData.images.length === 0) {
      toast.warning('At least one image is required');
      return;
    }
    
    if (!formData.thumbnailImage) {
      toast.warning('Please select a thumbnail image');
      return;
    }

    // Validate price (only if not free category)
    if (formData.category !== 'free') {
      if (formData.price === '') {
        toast.warning('Please enter a price for your item');
        return;
      }
      if (parseFloat(formData.price) < 0) {
        toast.warning('Price cannot be negative');
        return;
      }
    } else {
      // Ensure price is 0 for free items
      formData.price = 0;
    }

    console.log('✅ ListingForm: Form validation passed, submitting to parent component');
    onSubmit(formData);
  };

  // Monitor component lifecycle for debugging
  useEffect(() => {
    console.log('🔄 ListingForm: Component mounted or updated');
    
    return () => {
      console.log('🗑️ ListingForm: Component is unmounting');
    };
  }, []);

  // Monitor when the component might be getting reset or recreated
  useEffect(() => {
    console.log('📋 ListingForm: Form data changed:', {
      title: formData.title,
      hasImages: formData.images?.length > 0,
      hasVideo: !!formData.video,
      videoType: formData.video?.type
    });
  }, [formData]);

  // Media handlers
  const handleImagesChange = (images) => {
    console.log('🖼️ Images changed:', images.length, 'images');
    setFormData(prev => ({
      ...prev,
      images
    }));
  };

  const handleVideoChange = (video) => {
    console.log('🎥 ListingForm: Video change handler called:', {
      hasVideo: !!video,
      videoType: video?.type,
      videoUrl: video?.url?.substring(0, 50) + '...',
      isEditMode: !!initialData,
      currentFormDataVideo: formData.video?.type || 'none'
    });

    setFormData(prev => {
      const updated = {
        ...prev,
        video
      };
      console.log('🔄 ListingForm: Updated form data with new video:', {
        title: updated.title,
        hasVideo: !!updated.video,
        videoType: updated.video?.type
      });
      return updated;
    });

    console.log('✅ ListingForm: Video change handling completed');
  };

  const handleThumbnailChange = (thumbnailUrl) => {
    console.log('🖼️ Thumbnail changed:', thumbnailUrl?.substring(0, 50) + '...');
    setFormData(prev => ({
      ...prev,
      thumbnailImage: thumbnailUrl
    }));
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '2px solid #e4e3e2',
    fontSize: '1rem',
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    transition: 'all 0.2s ease',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  };

  const inputFocusStyle = {
    borderColor: '#881c1c',
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(136, 28, 28, 0.1)'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#881c1c',
    fontSize: '0.95rem'
  };

  const fieldStyle = {
    marginBottom: '1.5rem'
  };

  return (
    <div style={{
      fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      maxWidth: '100%'
    }}>
      <form onSubmit={handleSubmit}>
        {/* Title input field */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Title *</label>
          <TextField
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="What are you selling?"
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' }
            }}
          />
        </div>

        {/* Description textarea */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Description *</label>
          <TextField
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Describe your item in detail..."
            variant="outlined"
            fullWidth
            size="small"
            multiline
            minRows={4}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' }
            }}
          />
        </div>

        {/* Media Upload Section */}
        <MediaUpload
          onImagesChange={handleImagesChange}
          onVideoChange={handleVideoChange}
          onThumbnailChange={handleThumbnailChange}
          initialImages={formData.images}
          initialVideo={formData.video}
          initialThumbnail={formData.thumbnailImage}
        />

        {/* Two-column layout for category and condition */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {/* Category selection dropdown */}
          <div>
            <label style={labelStyle}>
              Category *
            </label>
            <FormControl fullWidth size="small">
              <InputLabel id="category-label" sx={{ display: 'none' }}>Category *</InputLabel>
              <Select
                labelId="category-label"
                name="category"
                value={formData.category}
                label="Category *"
                onChange={handleChange}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' }
                }}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="textbooks">Textbooks</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="clothing">Clothing</MenuItem>
                <MenuItem value="dorm-supplies">Dorm Supplies</MenuItem>
                <MenuItem value="creative-goods">Others</MenuItem>
                <MenuItem value="free">Free</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Condition selection dropdown */}
          <div>
            <label style={labelStyle}>Condition *</label>
            <FormControl fullWidth size="small">
              <InputLabel id="condition-label" sx={{ display: 'none' }}>Condition *</InputLabel>
              <Select
                labelId="condition-label"
                name="condition"
                value={formData.condition}
                label="Condition *"
                onChange={handleChange}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' }
                }}
                MenuProps={{
                  disablePortal: true
                }}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="like-new">Like New</MenuItem>
                <MenuItem value="good">Good</MenuItem>
                <MenuItem value="fair">Fair</MenuItem>
                <MenuItem value="poor">Poor</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Price input shown only if category not free */}
        {formData.category !== 'free' && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Price (USD) *</label>
            <FormControl fullWidth size="small">
              <OutlinedInput
                id="price-input"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#881c1c' }
                }}
                label="Price (USD) *"
              />
            </FormControl>
          </div>
        )}

        {/* Negotiable checkbox (hidden for free items) */}
        {formData.category !== 'free' && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="negotiable"
                  checked={formData.negotiable}
                  onChange={handleChange}
                  sx={{ color: '#881c1c', '&.Mui-checked': { color: '#881c1c' } }}
                />
              }
              label="Price is negotiable"
            />
          </Box>
        )}

        {/* Payment method is always direct in Phase 1 — no selector needed */}

        {/* Form action buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          paddingTop: '1rem',
          borderTop: '1px solid #e4e3e2'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: '#6c757d',
              border: '2px solid #e4e3e2',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
              transition: 'all 0.2s ease',
              minWidth: '120px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f0efeb';
              e.target.style.borderColor = '#6c757d';
              e.target.style.color = '#495057';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = '#e4e3e2';
              e.target.style.color = '#6c757d';
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#881c1c',
              color: 'white',
              border: '2px solid #881c1c',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
              transition: 'all 0.2s ease',
              minWidth: '120px',
              boxShadow: '0 2px 4px rgba(136, 28, 28, 0.2)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#a02424';
              e.target.style.borderColor = '#a02424';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(136, 28, 28, 0.3)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#881c1c';
              e.target.style.borderColor = '#881c1c';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(136, 28, 28, 0.2)';
            }}
          >
            {initialData ? 'Update Listing' : 'Create Listing'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ListingForm; 